import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, UploadCloud, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Logo } from "@/components/Logo";
import { Reveal } from "@/components/Reveal";
import { GraffitiTag } from "@/components/GraffitiTag";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-surface border border-white/10 focus:border-gold px-4 py-3 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors duration-300";

const STEPS = ["Company & Contact", "Operation Details", "Documents & Consent"];

const EQUIPMENT_OPTIONS = ["Dry Van", "Flatbed", "Box Truck", "Other"];
const STATUS_OPTIONS = ["Active — running under my own authority", "Active — leased on to a carrier", "New authority — awaiting activation", "Paused / not currently operating"];

const DocField = ({ id, label, hint, file, onChange }) => (
  <label
    htmlFor={id}
    className="block border border-dashed border-gold/40 hover:border-gold bg-surface p-5 cursor-pointer transition-colors duration-300"
  >
    <input
      id={id}
      data-testid={`${id}-input`}
      type="file"
      accept=".pdf,.png,.jpg,.jpeg,.webp"
      className="sr-only"
      onChange={(e) => onChange(e.target.files?.[0] || null)}
    />
    <div className="flex items-center gap-4">
      <UploadCloud size={22} className="text-gold shrink-0" />
      <div>
        <p className="text-sm text-white font-semibold">{label}</p>
        <p className="text-xs text-zinc-500 mt-0.5">{file ? file.name : hint}</p>
      </div>
    </div>
  </label>
);

export default function BecomeCarrier() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_person: "",
    phone: "",
    email: "",
    mc_number: "",
    usdot_number: "",
    equipment_type: "",
    num_trucks: "",
    preferred_lanes: "",
    operating_status: "",
    additional_info: "",
  });
  const [files, setFiles] = useState({ doc_w9: null, doc_insurance: null, doc_authority: null });
  const [consent, setConsent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.company_name.trim() || !form.contact_person.trim() || !form.phone.trim() || !form.email.trim()) {
        toast.error("Please complete company name, contact person, phone and email.");
        return false;
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
        toast.error("Please enter a valid email address.");
        return false;
      }
    }
    if (step === 1 && !form.equipment_type) {
      toast.error("Please select your equipment type.");
      return false;
    }
    return true;
  };

  const next = () => validateStep() && setStep((s) => Math.min(s + 1, 2));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!consent) {
      toast.error("Please consent to follow-up communication to submit.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("consent", "true");
      Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));
      await api.post("/carriers", fd);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main data-testid="carrier-success" className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16">
        <Seo title="Application Received | B&H Truck Dispatching" description="Thank you for your interest in partnering with B&H Truck Dispatching." />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="card-gold max-w-xl w-full p-10 sm:p-14 text-center"
        >
          <Logo size="lg" className="mx-auto mb-8" testId="success-logo" />
          <CheckCircle2 size={40} className="text-gold mx-auto mb-6" />
          <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase text-white mb-5">Application Received</h1>
          <p className="text-zinc-300 text-sm sm:text-base leading-relaxed">
            Thank you for your interest in partnering with B&amp;H Truck Dispatching. Our team will review your
            information and contact you regarding the next steps.
          </p>
        </motion.div>
      </main>
    );
  }

  return (
    <main data-testid="become-carrier-page" className="pt-32 pb-24 px-4 sm:px-8 lg:px-16 max-w-3xl mx-auto">
      <Seo
        title="Become a Carrier | B&H Truck Dispatching"
        description="Start your carrier onboarding with B&H Truck Dispatching. Tell us about your operation and upload your W-9, insurance, and authority documents securely."
      />
      <Reveal>
        <p className="overline-label mb-4 text-center">Carrier Registration Portal</p>
        <div className="relative inline-block">
          <GraffitiTag testId="carrier-graffiti-tag" className="absolute -top-8 sm:-top-10 -right-2 sm:-right-10 z-10 text-xl sm:text-3xl" rotate={-5}>
            Partner Up!
          </GraffitiTag>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white text-center leading-tight">
            Become a <span className="gold-text">Carrier</span>
          </h1>
        </div>
        <p className="mt-4 text-center text-sm sm:text-base text-zinc-400 max-w-xl mx-auto">
          Tell us about your trucking operation. Our team reviews every submission personally.
        </p>
      </Reveal>

      <div className="flex items-center justify-center gap-0 mt-12 mb-10" data-testid="form-progress">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-9 h-9 rounded-none border flex items-center justify-center font-accent text-sm font-bold transition-colors duration-300 ${
                  i <= step ? "border-gold text-gold bg-gold/10" : "border-white/15 text-zinc-500"
                }`}
              >
                {i + 1}
              </div>
              <span className={`mt-2 text-[10px] uppercase tracking-wider hidden sm:block ${i <= step ? "text-champagne" : "text-zinc-600"}`}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && <div className={`w-10 sm:w-20 h-px mx-2 mb-0 sm:mb-5 ${i < step ? "bg-gold" : "bg-white/10"}`} />}
          </div>
        ))}
      </div>

      <div className="card-gold p-6 sm:p-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === 0 && (
              <div className="grid sm:grid-cols-2 gap-5" data-testid="carrier-step-1">
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Carrier / Company Name *</label>
                  <input data-testid="company-name-input" className={inputCls} value={form.company_name} onChange={set("company_name")} placeholder="e.g. Smith Trucking LLC" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Contact Person *</label>
                  <input data-testid="contact-person-input" className={inputCls} value={form.contact_person} onChange={set("contact_person")} placeholder="Full name" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Phone Number *</label>
                  <input data-testid="phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Email Address *</label>
                  <input data-testid="email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-5" data-testid="carrier-step-2">
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">MC Number</label>
                  <input data-testid="mc-number-input" className={inputCls} value={form.mc_number} onChange={set("mc_number")} placeholder="MC-000000" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">USDOT Number</label>
                  <input data-testid="usdot-number-input" className={inputCls} value={form.usdot_number} onChange={set("usdot_number")} placeholder="0000000" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Equipment Type *</label>
                  <select data-testid="equipment-type-select" className={inputCls} value={form.equipment_type} onChange={set("equipment_type")}>
                    <option value="">Select equipment</option>
                    {EQUIPMENT_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Number of Trucks</label>
                  <input data-testid="num-trucks-input" type="number" min="1" className={inputCls} value={form.num_trucks} onChange={set("num_trucks")} placeholder="1" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Preferred Freight Lanes</label>
                  <input data-testid="preferred-lanes-input" className={inputCls} value={form.preferred_lanes} onChange={set("preferred_lanes")} placeholder="e.g. Southeast regional, TX to FL" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Current Operating Status</label>
                  <select data-testid="operating-status-select" className={inputCls} value={form.operating_status} onChange={set("operating_status")}>
                    <option value="">Select status</option>
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5" data-testid="carrier-step-3">
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Uploads are optional at this stage and stored privately. Accepted formats: PDF, PNG, JPG, WEBP (max 15MB each).
                </p>
                <DocField id="doc-w9" label="W-9 Form" hint="Upload your completed W-9" file={files.doc_w9} onChange={(f) => setFiles((s) => ({ ...s, doc_w9: f }))} />
                <DocField id="doc-insurance" label="Certificate of Insurance" hint="Upload your current COI" file={files.doc_insurance} onChange={(f) => setFiles((s) => ({ ...s, doc_insurance: f }))} />
                <DocField id="doc-authority" label="Operating Authority" hint="Upload your MC authority document" file={files.doc_authority} onChange={(f) => setFiles((s) => ({ ...s, doc_authority: f }))} />
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400 block mb-2">Additional Information</label>
                  <textarea data-testid="additional-info-input" rows={4} className={inputCls} value={form.additional_info} onChange={set("additional_info")} placeholder="Anything else we should know about your operation" />
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-label">
                  <input
                    type="checkbox"
                    data-testid="consent-checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 accent-[#D4AF37] w-4 h-4"
                  />
                  <span className="text-xs text-zinc-400 leading-relaxed">
                    I consent to B&amp;H Truck Dispatching contacting me by phone, text, or email regarding my inquiry and
                    the next steps in the onboarding process.
                  </span>
                </label>
                <p className="text-[11px] text-zinc-600 leading-relaxed">
                  Privacy notice: your information and documents are stored securely, used only to evaluate your carrier
                  application, and are never sold or shared outside of onboarding purposes.
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-white/5">
          {step > 0 ? (
            <button data-testid="carrier-back-btn" onClick={back} className="btn-outline-gold !px-6 !py-3">
              <ArrowLeft size={16} /> Back
            </button>
          ) : (
            <span />
          )}
          {step < 2 ? (
            <button data-testid="carrier-next-btn" onClick={next} className="btn-gold !px-6 !py-3">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button data-testid="carrier-submit-btn" onClick={submit} disabled={submitting} className="btn-gold !px-6 !py-3 disabled:opacity-50 disabled:pointer-events-none">
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
